import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { mergeIds, PaginationState } from '@redux/helpers';
import { ChatMessage } from '@typings/show';
import { CHAT_MESSAGES_PER_PAGE } from '@utils/constants';

type InitialState = {
  byShowId: Record<string, PaginationState>;
  byId: Record<string, ChatMessage>;
  pinnedByShowId: Record<string, string | null>;
};

export const initialState: InitialState = {
  byShowId: {},
  byId: {},
  pinnedByShowId: {},
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatMessageById: (
      state,
      action: PayloadAction<Partial<ChatMessage>>,
    ) => {
      const message = action.payload;
      if (message.id) {
        state.byId[message.id] = {
          ...(state.byId[message.id] || {}),
          ...message,
        };
      }
    },
    setChatMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      const messages = action.payload;
      if (messages.length) {
        messages.forEach((message) => {
          const previousData = state.byId[message.id] || {};
          state.byId[message.id] = {
            ...previousData,
            ...message,
          };
        });
      }
    },
    setChatMessagesByShowId: (
      state,
      action: PayloadAction<{
        showId: string;
        ids: string[];
        isRefresh: boolean;
      }>,
    ) => {
      const { showId, ids, isRefresh } = action.payload;
      const oldIds = state.byShowId[showId]?.ids || [];

      state.byShowId[showId] = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === CHAT_MESSAGES_PER_PAGE,
      };
    },
    addMessageToShowIds: (
      state,
      action: PayloadAction<{ showId: string; id: string }>,
    ) => {
      const { showId, id } = action.payload;
      const oldIds = state.byShowId[showId]?.ids || [];

      state.byShowId[showId] = {
        ...state.byShowId[showId],
        ids: Array.from(new Set([id, ...oldIds])),
      };
    },
    replaceOptimisticChatMessageByShowId: (
      state,
      action: PayloadAction<{
        showId: string;
        optimisticId: string;
        realMessageId?: string;
        isSuccess: boolean;
      }>,
    ) => {
      const { showId, optimisticId, realMessageId, isSuccess } = action.payload;
      if (state.byShowId[showId]) {
        const oldIds = [...(state.byShowId[showId]?.ids || [])];
        if (isSuccess && realMessageId) {
          const index = oldIds.findIndex((id) => id === optimisticId);
          if (index !== -1) {
            oldIds[index] = realMessageId;
          }
          state.byShowId[showId] = {
            ...state.byShowId[showId],
            ids: oldIds,
          };

          if (state.byId[realMessageId]) {
            delete state.byId[optimisticId];
          }
        } else {
          state.byShowId[showId] = {
            ...state.byShowId[showId],
            ids: oldIds.filter((id) => id !== optimisticId),
          };

          delete state.byId[optimisticId];
        }
      }
    },
    updatePinnedChatMessageByShowId: (
      state,
      action: PayloadAction<{ showId: string; id: string | null }>,
    ) => {
      const { showId, id } = action.payload;
      const existedPinnedChatMessageId = state.pinnedByShowId?.[showId];

      if (existedPinnedChatMessageId && id !== existedPinnedChatMessageId) {
        state.byId[existedPinnedChatMessageId] = {
          ...state.byId[existedPinnedChatMessageId],
          pinned: false,
        };
      }

      state.pinnedByShowId[showId] = id;
    },
    clearChatMessages: (state) => {
      state.byShowId = {};
      state.byId = {};
      state.pinnedByShowId = {};
    },
  },
});

export const {
  setChatMessageById,
  setChatMessages,
  setChatMessagesByShowId,
  addMessageToShowIds,
  updatePinnedChatMessageByShowId,
  clearChatMessages,
} = chatSlice.actions;

export default chatSlice.reducer;
